import type React from "react"
import { User, Phone, Edit, Save, X } from "lucide-react"
import { useEffect, useState } from "react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Select } from "../ui/select"
import { LEAD_SOURCE_FORM_OPTIONS } from "../../constants/leadSources"
import { formatLeadStatusLabel } from "../../constants/leadStatus"

interface LeadInformation {
  first_name: string
  last_name: string
  phone: string
  // COMMENTED OUT - Email field temporarily disabled
  email?: string
  source: string
}

interface EmailValidation {
  emailChecking: boolean
  emailAvailable: boolean | null
  emailMessage: string
}

interface PhoneValidation {
  phoneChecking: boolean
  phoneAvailable: boolean | null
  phoneMessage: string
}

interface LeadInformationCardProps {
  leadData: LeadInformation
  viewOnly?: boolean
  errors?: { [key: string]: string[] }
  onDataChange?: (field: keyof LeadInformation, value: string) => void
  onEdit?: () => void
  onSave?: (data: LeadInformation) => void
  onCancel?: () => void
  isEditing?: boolean
  alwaysEditable?: boolean
  emailValidation?: EmailValidation
  phoneValidation?: PhoneValidation
  onPhoneFormatValidationChange?: (hasError: boolean) => void
  onPhoneClick?: (leadData: LeadInformation) => void
  status?: string
}

export default function LeadInformationCard({ 
  leadData, 
  viewOnly = false, 
  errors = {}, 
  onDataChange,
  onEdit,
  onSave,
  onCancel,
  isEditing = false,
  alwaysEditable = false,
  emailValidation,
  phoneValidation,
  onPhoneFormatValidationChange,
  onPhoneClick,
  status,
}: LeadInformationCardProps) {
  // State for debounced phone format validation
  const [debouncedPhoneFormatError, setDebouncedPhoneFormatError] = useState<string | null>(null)
  
  const sourceOptions = LEAD_SOURCE_FORM_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
  }))

  const handleInputChange = (field: keyof LeadInformation, value: string) => {
    if (onDataChange) {
      onDataChange(field, value)
    }
    
    // Real-time validation for length limits and phone format
    if (field === 'first_name' && value.length > 50) {
      // This will be handled by the parent component's error state
    } else if (field === 'last_name' && value.length > 50) {
      // This will be handled by the parent component's error state
    } else if (field === 'email') {
      // Real-time email validation - only check length, format validation is handled by parent component
      if (value && value.length > 255) {
        // This will be handled by the parent component's error state
      }
    } else if (field === 'phone') {
      // Real-time phone validation - check if it contains invalid characters
      const phoneWithoutPlus = value.startsWith('+') ? value.slice(1) : value
      if (phoneWithoutPlus && !/^\d*$/.test(phoneWithoutPlus)) {
        // This will be handled by the parent component's error state
      }
    }
  }

  // Phone format validation with smart debouncing
  useEffect(() => {
    const getPhoneFormatValidation = () => {
      if (!leadData.phone || leadData.phone.length === 0) return null
      
      const phoneWithoutPlus = leadData.phone.startsWith('+') ? leadData.phone.slice(1) : leadData.phone
      
      // Check if phone has non-numeric characters
      if (!/^\d+$/.test(phoneWithoutPlus)) {
        return "Phone number must contain only numbers (with optional + prefix)"
      }
      
      // Check if phone is too short
      if (phoneWithoutPlus.length < 10) {
        return "Phone number must have at least 10 digits"
      }
      
      // Check if phone is too long
      if (phoneWithoutPlus.length > 15) {
        return "Phone number cannot exceed 15 digits"
      }
      
      return null
    }

    // Always check current validation first
    const currentValidation = getPhoneFormatValidation()
    
    // If input is empty or valid, clear any existing error immediately
    if (!leadData.phone || leadData.phone.length === 0 || !currentValidation) {
      setDebouncedPhoneFormatError(null)
      // Notify parent that there's no format error
      if (onPhoneFormatValidationChange) {
        onPhoneFormatValidationChange(false)
      }
      return
    }

    // If current input is invalid, set error after delay
    const timer = setTimeout(() => {
      setDebouncedPhoneFormatError(currentValidation)
      // Notify parent that there is a format error
      if (onPhoneFormatValidationChange) {
        onPhoneFormatValidationChange(true)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [leadData.phone, onPhoneFormatValidationChange])

  // COMMENTED OUT - Email field styling and icon functions temporarily disabled
  // These functions are kept for potential future use when email field is re-enabled
  /*
  const getEmailFieldStyle = () => {
    if (!emailValidation) return errors.email ? "border-red-500" : ""
    
    if (emailValidation.emailChecking) return "border-slate-300"
    if (emailValidation.emailAvailable === true) return "border-green-500"
    if (emailValidation.emailAvailable === false) return "border-red-500"
    if (emailValidation.emailMessage && emailValidation.emailMessage !== "Email is available" && emailValidation.emailMessage !== "Email is already in use by another lead") {
      return "border-red-500" // Format validation error
    }
    return errors.email ? "border-red-500" : ""
  }

  const getEmailIcon = () => {
    if (!emailValidation) return null
    
    if (emailValidation.emailChecking) {
      return <div className="h-4 w-4 text-slate-400 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
    }
    if (emailValidation.emailAvailable === true) {
      return <div className="h-4 w-4 text-green-500">✓</div>
    }
    if (emailValidation.emailAvailable === false) {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    // Show error icon for format validation errors
    if (emailValidation.emailMessage && emailValidation.emailMessage !== "Email is available" && emailValidation.emailMessage !== "Email is already in use by another lead") {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    return null
  }
  */

  // Get phone field styling based on validation state
  const getPhoneFieldStyle = () => {
    // Check for debounced format validation errors first
    if (debouncedPhoneFormatError) return "border-red-500"
    
    // Check for general errors
    if (errors.phone && errors.phone.length > 0) return "border-red-500"
    
    // If no phoneValidation, return default
    if (!phoneValidation) return ""
    
    // Check availability validation states
    if (phoneValidation.phoneChecking) return "border-slate-300"
    if (phoneValidation.phoneAvailable === true) return "border-green-500 focus:border-green-500 focus:ring-green-500"
    if (phoneValidation.phoneAvailable === false) return "border-red-500"
    if (phoneValidation.phoneMessage && phoneValidation.phoneMessage !== "Phone number is available" && phoneValidation.phoneMessage !== "Phone number is already in use by another lead") {
      return "border-red-500" // Format validation error
    }
    
    return ""
  }

  // Get phone validation icon
  const getPhoneIcon = () => {
    // Show error icon for backend validation errors (highest priority)
    if (errors.phone && errors.phone.length > 0) {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    
    // Show error icon for format validation errors
    if (debouncedPhoneFormatError) {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    
    // Handle phoneValidation states
    if (!phoneValidation) return null
    
    if (phoneValidation.phoneChecking) {
      return <div className="h-4 w-4 text-slate-400 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
    }
    if (phoneValidation.phoneAvailable === true) {
      return <div className="h-4 w-4 text-green-500">✓</div>
    }
    if (phoneValidation.phoneAvailable === false) {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    // Show error icon for availability validation errors
    if (phoneValidation.phoneMessage && phoneValidation.phoneMessage !== "Phone number is available" && phoneValidation.phoneMessage !== "Phone number is already in use by another lead") {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    return null
  }

  return (
    <Card>
      <CardHeader className="border-b border-slate-200">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Lead Information
          </CardTitle>
          {!alwaysEditable && viewOnly && !isEditing && onEdit && (
            <Button onClick={onEdit} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {!alwaysEditable && viewOnly && isEditing && (
            <div className="flex items-center gap-2">
              <Button onClick={onCancel} size="sm" variant="outline" className="border-slate-300 hover:border-slate-400">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={() => onSave?.(leadData)} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="first_name">First Name *</Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="first_name"
                value={leadData.first_name}
                onChange={(e) => handleInputChange("first_name", e.target.value)}
                placeholder="Enter first name"
                className={errors.first_name ? "border-red-500" : ""}
                disabled={viewOnly && !isEditing && !alwaysEditable}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{leadData.first_name}</p> 
              </div>
            )}
            {errors.first_name && <p className="text-sm text-red-500">{errors.first_name[0]}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="last_name">Last Name *</Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="last_name"
                value={leadData.last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                placeholder="Enter last name"
                className={errors.last_name ? "border-red-500" : ""}
                disabled={viewOnly && !isEditing && !alwaysEditable}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{leadData.last_name}</p> 
              </div>
            )}
            {errors.last_name && <p className="text-sm text-red-500">{errors.last_name[0]}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            {(isEditing || alwaysEditable) ? (
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="phone"
                  value={leadData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className={`pl-10 pr-10 ${getPhoneFieldStyle()}`}
                  disabled={viewOnly && !isEditing && !alwaysEditable}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {getPhoneIcon()}
                </div>
              </div>
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                {onPhoneClick ? (
                  <button
                    onClick={() => onPhoneClick(leadData)}
                    className="text-sm text-slate-700 hover:text-slate-900 transition-colors truncate hover:underline cursor-pointer text-left w-full"
                  >
                    +{leadData.phone}
                  </button>
                ) : (
                  <p className="text-sm text-slate-900">{leadData.phone}</p>
                )}
              </div>
            )}
            {/* Phone validation messages - show only one error at a time */}
            {(phoneValidation || debouncedPhoneFormatError || errors.phone) && (
              <div className="space-y-1">
                {/* Priority 1: Format validation errors */}
                {debouncedPhoneFormatError && (
                  <p className="text-sm text-red-600">✗ {debouncedPhoneFormatError}</p>
                )}
                
                {/* Priority 2: Backend errors (only if no format errors) */}
                {!debouncedPhoneFormatError && errors.phone && errors.phone.length > 0 && (
                  <p className="text-sm text-red-600">✗ {errors.phone[0]}</p>
                )}
                
                {/* Priority 3: Availability validation messages (only if no other errors) */}
                {phoneValidation && !debouncedPhoneFormatError && (!errors.phone || errors.phone.length === 0) && (
                  <>
                    {phoneValidation.phoneChecking && (
                      <p className="text-sm text-blue-600">Checking phone availability...</p>
                    )}
                    {!phoneValidation.phoneChecking && phoneValidation.phoneAvailable === true && (
                      <p className="text-sm text-green-600">✓ {phoneValidation.phoneMessage}</p>
                    )}
                    {!phoneValidation.phoneChecking && phoneValidation.phoneAvailable === false && (
                      <p className="text-sm text-red-600">✗ {phoneValidation.phoneMessage}</p>
                    )}
                    {!phoneValidation.phoneChecking && phoneValidation.phoneAvailable === null && phoneValidation.phoneMessage && (
                      <p className="text-sm text-amber-600">{phoneValidation.phoneMessage}</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* COMMENTED OUT - Email field temporarily disabled */}
          {/*
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            {(isEditing || alwaysEditable) ? (
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={leadData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="email@example.com"
                  className={`pl-10 pr-10 ${getEmailFieldStyle()}`}
                  disabled={viewOnly && !isEditing && !alwaysEditable}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {getEmailIcon()}
                </div>
              </div>
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{leadData.email}</p> 
              </div>
            )}
            {/* Email validation message */}
            {/*
            {emailValidation && emailValidation.emailMessage && (
              <p className={`text-sm ${
                emailValidation.emailAvailable === true ? 'text-green-600' : 
                emailValidation.emailAvailable === false ? 'text-red-600' : 
                emailValidation.emailMessage.includes('valid') || emailValidation.emailMessage.includes('cannot') ? 'text-red-600' : 
                'text-slate-600'
              }`}>
                {emailValidation.emailChecking ? "Checking email availability..." : emailValidation.emailMessage}
              </p>
            )}
            {errors.email && <p className="text-sm text-red-500">{errors.email[0]}</p>}
          </div>
          */}

          <div className="space-y-2">
            <Label>Status</Label>
            <div className="p-2 bg-slate-50 rounded-md border border-slate-200 min-h-[42px] flex items-center">
              <p className="text-sm text-slate-900">{formatLeadStatusLabel(status)}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="source">Source</Label>
            {(isEditing || alwaysEditable) ? (
              <Select 
                value={leadData.source} 
                onValueChange={(value) => handleInputChange("source", value)} 
                className="w-full"
              >
                {sourceOptions.map((record) => (
                  <Select.Option key={record.value} value={record.value}>
                    {record.label}
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">
                  {sourceOptions.find(option => option.value === leadData.source)?.label || leadData.source}
                </p> 
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 